import { useRef, useState } from "react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { AddressMap } from "@/features/canteiros/components/address-map";
import { CepInput } from "@/features/canteiros/components/cep-input";
import { useCepLookup } from "@/features/canteiros/hooks/use-cep-lookup.ts";
import { useGeocodeAddress } from "@/features/canteiros/hooks/use-geocode-address.ts";
import type { OnboardingAddress } from "../../types";
import type { StructureViolations } from "../../utils/violations";
import { FieldError } from "../field-error";
import { FieldLabel } from "../field-label";

interface AddressSectionProps {
	value: OnboardingAddress;
	onChange: (next: OnboardingAddress) => void;
	violations?: StructureViolations;
	idPrefix: string;
}

export function AddressSection({
	value,
	onChange,
	violations,
	idPrefix,
}: AddressSectionProps) {
	const cepLookup = useCepLookup();
	const { mutate: runCepLookup } = cepLookup;
	const { mutate: runGeocode } = useGeocodeAddress();
	const lastLookedUp = useRef("");
	const [center, setCenter] = useState<{ lat: number; lng: number }>();
	const [cepUnico, setCepUnico] = useState(false);
	// Latest value, for async callbacks (CEP lookup / map) that would otherwise
	// close over a stale snapshot.
	const valueRef = useRef(value);
	valueRef.current = value;

	const set = (patch: Partial<OnboardingAddress>) =>
		onChange({ ...valueRef.current, ...patch });

	// Look up the CEP when the user finishes editing it (on blur), never on a
	// programmatic restore — so navigating back to a filled step doesn't re-fetch
	// and clobber the saved address. The ref dedupes repeated blurs.
	function handleCepBlur() {
		const digits = valueRef.current.zipcode.replace(/\D/g, "");
		if (digits.length !== 8 || digits === lastLookedUp.current) return;
		lastLookedUp.current = digits;
		runCepLookup(digits, {
			onSuccess: (address) => {
				const current = valueRef.current;
				// A "CEP único" (one CEP for a whole town) comes back without a
				// street. Unlock street + neighborhood for manual entry and clear any
				// stale values from a previous CEP; city/state stay CEP-driven.
				const unico = !address.street;
				setCepUnico(unico);
				onChange({
					...current,
					street: unico ? "" : address.street,
					neighborhood: unico
						? ""
						: address.neighborhood || current.neighborhood,
					city: address.city || current.city,
					state: address.state || current.state,
					lat: typeof address.lat === "number" ? address.lat : current.lat,
					lng: typeof address.lng === "number" ? address.lng : current.lng,
				});
				// BrasilAPI didn't geocode this CEP — center the map on the returned
				// address so the user only has to click the exact spot.
				if (
					typeof address.lat !== "number" ||
					typeof address.lng !== "number"
				) {
					runGeocode(
						{
							street: address.street,
							city: address.city,
							state: address.state,
							zipcode: digits,
						},
						{ onSuccess: (point) => point && setCenter(point) },
					);
				}
			},
			onError: () => {
				toast.error("CEP não encontrado. Preencha o endereço manualmente.");
			},
		});
	}

	return (
		<>
			<div className="onboarding-field">
				<FieldLabel>CEP</FieldLabel>
				<CepInput
					id={`${idPrefix}-zipcode`}
					value={value.zipcode}
					aria-invalid={!!violations?.zipcode}
					onAccept={(zipcode) => set({ zipcode })}
					onBlur={handleCepBlur}
				/>
				{cepLookup.isPending && <p className="form-hint">Buscando endereço…</p>}
				{cepUnico && (
					<p className="form-hint">
						CEP geral: informe a rua e o bairro manualmente.
					</p>
				)}
				<FieldError message={violations?.zipcode} />
			</div>

			<div className="onboarding-field-row">
				<div className="onboarding-field">
					<FieldLabel>Rua</FieldLabel>
					<Input
						id={`${idPrefix}-street`}
						placeholder="Av. Principal"
						value={value.street}
						readOnly={!cepUnico}
						aria-invalid={!!violations?.street}
						onChange={(event) => set({ street: event.target.value })}
					/>
					<FieldError message={violations?.street} />
				</div>
				<div className="onboarding-field">
					<FieldLabel>Número</FieldLabel>
					<Input
						id={`${idPrefix}-number`}
						placeholder="1000"
						value={value.number}
						aria-invalid={!!violations?.number}
						onChange={(event) => set({ number: event.target.value })}
					/>
					<FieldError message={violations?.number} />
				</div>
			</div>

			<div className="onboarding-field">
				<FieldLabel>Complemento (opcional)</FieldLabel>
				<Input
					id={`${idPrefix}-complement`}
					placeholder="Bloco, sala, referência"
					value={value.complement}
					onChange={(event) => set({ complement: event.target.value })}
				/>
			</div>

			<div className="onboarding-field-row">
				<div className="onboarding-field">
					<FieldLabel>Bairro</FieldLabel>
					<Input
						id={`${idPrefix}-neighborhood`}
						placeholder="Centro"
						value={value.neighborhood}
						readOnly={!cepUnico}
						aria-invalid={!!violations?.neighborhood}
						onChange={(event) => set({ neighborhood: event.target.value })}
					/>
					<FieldError message={violations?.neighborhood} />
				</div>
				<div className="onboarding-field">
					<FieldLabel>Cidade</FieldLabel>
					<Input
						id={`${idPrefix}-city`}
						placeholder="Natal"
						value={value.city}
						readOnly
						aria-invalid={!!violations?.city}
						onChange={(event) => set({ city: event.target.value })}
					/>
					<FieldError message={violations?.city} />
				</div>
			</div>

			<div className="onboarding-field-row">
				<div className="onboarding-field">
					<FieldLabel>UF</FieldLabel>
					<Input
						id={`${idPrefix}-state`}
						placeholder="RN"
						maxLength={2}
						value={value.state}
						readOnly
						aria-invalid={!!violations?.state}
						onChange={(event) => set({ state: event.target.value })}
					/>
					<FieldError message={violations?.state} />
				</div>
			</div>

			<div className="onboarding-field">
				<AddressMap
					lat={value.lat}
					lng={value.lng}
					center={center}
					onChange={(lat, lng) => onChange({ ...valueRef.current, lat, lng })}
				/>
			</div>
		</>
	);
}
